import { type MailRibbonControlId } from 'owa-ribbon-ids/lib/mailRibbonId';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list';
import { lazyShowCategoryDialog } from 'owa-categories';
import setInitialCategoryDialogViewState from 'owa-categories/lib/actions/setInitialCategoryDialogViewState';
import type CategoryActionSource from 'owa-categories/lib/utils/CategoryActionSource';
import type { MailboxInfo } from 'owa-client-types';
import { getActionSourceFromCommandingViewMode } from 'owa-command-ribbon';
import { lazyAddFavoriteCategory } from 'owa-favorites';
import { resetFocus } from 'owa-mail-focus-manager';
import { getSelectedTableView } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import {
    lazyAddCategoriesFromTable,
    lazyClearCategoriesFromTable,
    lazyRemoveCategoriesFromTable,
    lazyAddCategoriesFromItem,
    lazyClearCategoriesFromItem,
    lazyRemoveCategoriesFromItem,
} from 'owa-mail-triage-action';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import type { ClientItemId } from 'owa-client-ids';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getReadRibbonId } from 'owa-mail-ribbon-utils/lib/getReadRibbonId';
import setCategoryMenuFindText from 'owa-categories/lib/actions/setCategoryMenuFindText';
import setShouldShowAllCategories from 'owa-categories/lib/actions/setShouldShowAllCategories';
import { logUsage } from 'owa-analytics';
import {
    type UTCalendarEventFeatureAction,
    type UTCalendarUIContext,
    type UTCalendarOrigin,
} from 'owa-unified-telemetry';
import { getMailCategorizeCallbacks } from 'owa-calendar-unified-telemetry-utils/lib/utils/logMailCategorizeTelemetry';
import { getRibbonViewMode } from 'owa-command-ribbon-store';
import { type CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import { isPopout } from 'owa-popout-v2';
import { isMeetingMessage } from 'owa-meeting-message-utils/lib/utils/isMeetingMessage';
import { getSingleSelectedItemFromView } from './getSelectedItemFromView';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';

/**
 * Wrapper function that handles item lookup, meeting message checks, and telemetry logging for mail category actions
 * @param action The category action being performed
 * @param itemId Optional item ID to target specific item
 */
function logCategorizeUnifiedTelemetry(
    action: UTCalendarEventFeatureAction,
    itemId?: ClientItemId
): void {
    let uiContext: UTCalendarUIContext;
    let currentItem;

    const ribbonViewMode = getRibbonViewMode();
    const origin: UTCalendarOrigin = ribbonViewMode === 1 ? 'SimplifiedRibbon' : 'ClassicRibbon';

    if (isPopout(window)) {
        uiContext = 'MailReadFormPopout';
    } else {
        uiContext = 'MailList';
    }

    if (itemId) {
        currentItem = getMailStore().items.get(itemId.Id);
    } else {
        currentItem = getSingleSelectedItemFromView(getSelectedTableView());
    }

    // Only log telemetry for meeting messages
    if (currentItem && isMeetingMessage(currentItem)) {
        const { onSuccess } = getMailCategorizeCallbacks(action, uiContext, origin);
        onSuccess();
    }
}

/**
 * Function to handle a category clicked.
 *
 * @param categoryName: name of category
 * @param isChecked: whether category is currently applied on the item(s)
 *
 */
export const onCategoryClicked = (
    categoryName: string,
    isChecked: boolean,
    itemId: ClientItemId | undefined,
    mailboxInfo: MailboxInfo | undefined
): void => {
    const tableView = getSelectedTableView();
    mailboxInfo = itemId?.mailboxInfo ?? mailboxInfo ?? getMailboxInfo(tableView);

    // Keep the ribbon categorize menu open when multi-select is enabled; otherwise dismiss.
    if (!isGlobalFeatureEnabled('cal-multiSelectCategory')) {
        resetFocus('ribbonCategoryMenuDismissed');
    }

    // Get telemetry callbacks for category action
    const action = isChecked ? 'UnselectItem' : 'SelectItem';
    logCategorizeUnifiedTelemetry(action, itemId);

    if (itemId !== undefined) {
        if (isChecked) {
            lazyRemoveCategoriesFromItem.importAndExecute(
                itemId.Id,
                mailboxInfo,
                [categoryName],
                getActionSourceFromCommandingViewMode()
            );
        } else {
            lazyAddCategoriesFromItem.importAndExecute(
                itemId.Id,
                mailboxInfo,
                [categoryName],
                undefined,
                getActionSourceFromCommandingViewMode()
            );
        }
    } else {
        const rowKeys = tableView.isInVirtualSelectAllMode
            ? tableView.rowKeys
            : [...tableView.selectedRowKeys.keys()];

        if (isChecked) {
            // Checked category is clicked, remove category.
            lazyRemoveCategoriesFromTable.importAndExecute(
                rowKeys,
                tableView,
                [categoryName],
                getActionSourceFromCommandingViewMode()
            );
        } else {
            // Unchecked category is clicked, add category.
            lazyAddCategoriesFromTable.importAndExecute(
                rowKeys,
                tableView,
                [categoryName],
                getActionSourceFromCommandingViewMode()
            );
        }
    }
};

/**
 * Shows the Category Dialog to allow user to create a new category
 */
export const onNewCategoryClicked = (
    tabId: string | undefined,
    windowRetriever: (tabId: string | undefined) => Window,
    categoryName: string | undefined,
    mailboxInfo: MailboxInfo | undefined,
    itemId: ClientItemId | undefined
): void => {
    logCategorizeUnifiedTelemetry('New', itemId);

    const tableView = getSelectedTableView();
    const rowKeys = tableView.isInVirtualSelectAllMode
        ? tableView.rowKeys
        : [...tableView.selectedRowKeys.keys()];
    const targetWindow = windowRetriever(tabId);
    mailboxInfo = itemId?.mailboxInfo ?? mailboxInfo ?? getMailboxInfo(tableView);

    const addFavoriteCategoryCallback = (
        categoryId: string,
        actionSource: CategoryActionSource,
        favoriteMailboxInfo: MailboxInfo
    ) => {
        lazyAddFavoriteCategory
            .import()
            .then(addFavoriteCategory =>
                addFavoriteCategory(categoryId, actionSource, favoriteMailboxInfo)
            );
    };

    const onAfterCreateNewCategory = (category?: string) => {
        // When a new category is created, we should apply it optimistically to the selected items
        if (itemId) {
            lazyAddCategoriesFromItem.importAndExecute(
                itemId.Id,
                mailboxInfo,
                category ? [category] : [],
                undefined,
                getActionSourceFromCommandingViewMode()
            );
        } else {
            lazyAddCategoriesFromTable.importAndExecute(
                rowKeys,
                tableView,
                category ? [category] : [],
                getActionSourceFromCommandingViewMode()
            );
        }
    };

    setInitialCategoryDialogViewState('new', categoryName);
    lazyShowCategoryDialog.importAndExecute({
        actionSource: getActionSourceFromCommandingViewMode(),
        addFavoriteCategory: addFavoriteCategoryCallback,
        onAfterCreateNewCategory,
        mailboxInfo,
        targetWindow,
    });
};

/**
 * Clears all categories on the selected mail item(s)
 */
export const onClearCategoriesClicked = (
    mailboxInfo: MailboxInfo | undefined,
    itemId: ClientItemId | undefined
): void => {
    const tableView = getSelectedTableView();
    mailboxInfo = itemId?.mailboxInfo ?? mailboxInfo ?? getMailboxInfo(tableView);
    resetFocus('ribbonClearCategoriesClicked');

    logCategorizeUnifiedTelemetry('Clear', itemId);
    if (itemId) {
        lazyClearCategoriesFromItem.importAndExecute(itemId.Id, mailboxInfo);
    } else {
        const rowKeys = tableView.isInVirtualSelectAllMode
            ? tableView.rowKeys
            : [...tableView.selectedRowKeys.keys()];

        lazyClearCategoriesFromTable.importAndExecute(
            rowKeys,
            tableView,
            getActionSourceFromCommandingViewMode()
        );
    }
};

/**
 * Shows the manage categories dialog
 */
export const onManageCategoriesClicked = (
    mailboxInfo: MailboxInfo,
    itemId: ClientItemId | undefined
): void => {
    logUsage('TnS_Category_ManageCategoriesRibbon', mailboxInfo);

    logCategorizeUnifiedTelemetry('Manage', itemId);

    if (isMonarchMultipleAccountsEnabled() || isGlobalFeatureEnabled('acct-multiacctcomingsoon')) {
        lazyMountAndShowFullOptions.importAndExecute(
            'accounts-category',
            'categories',
            undefined,
            undefined,
            itemId?.mailboxInfo ?? mailboxInfo
        );
    } else {
        lazyMountAndShowFullOptions.importAndExecute('accounts-category', 'categories');
    }
};

export const onAllCategoriesClicked = (
    mailboxInfo: MailboxInfo,
    itemId: ClientItemId | undefined
): void => {
    logUsage('TnS_Category_AllCategoriesRibbon', mailboxInfo);
    const ele: HTMLElement | null = document.getElementById(
        getReadRibbonId(626, itemId).toString()
    );

    if (ele) {
        ele.focus();
    }

    resetCategorizeSearch(true /*showAllCategories*/);
};

export const resetCategorizeSearch = (showAllCategories: boolean | undefined): void => {
    setCategoryMenuFindText('');
    setShouldShowAllCategories(showAllCategories ?? false);
};
